import React, { useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const NAV = [
  { id: "dashboard", label: "Dashboard", path: "/",        icon: <GridIcon /> },
  { id: "admin",     label: "Admin",     path: "/adminhanuman",   icon: <ShieldIcon />, adminOnly: true },
  { id: "payments",  label: "Payments",  path: "/payments",icon: <PayIcon />,   adminOnly: true },
  { id: "broadcast", label: "Broadcast", path: "/broadcast",icon: <BroadcastIcon />, adminOnly: true },
];

export default function Layout({ children, onLogout, userInfo = {} }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const page      = location.pathname;

  const [avatar, setAvatar] = useState(() => localStorage.getItem("bc_avatar") || null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [serverOnline, setServerOnline] = useState(true);
  useEffect(() => {
    const API = (process.env.REACT_APP_API_URL || "");
    const token = () => localStorage.getItem("token") || "";
    let failCount = 0;
    async function checkServer() {
      try {
        const r = await fetch(API + "/api/agents", {
          headers: { Authorization: "Bearer " + token() }
        });
        if (r.ok) { failCount = 0; setServerOnline(true); }
        else { failCount++; if (failCount >= 3) setServerOnline(false); }
      } catch {
        failCount++;
        if (failCount >= 3) setServerOnline(false);
      }
    }
    checkServer();
    const i = setInterval(checkServer, 15000);
    return () => clearInterval(i);
  }, []);

  const fileRef = useRef();

  // Custom draggable scrollbar for .adm-content (kept from the original —
  // some browsers/privacy settings suppress ::-webkit-scrollbar styling
  // entirely, so this draws and drives its own thumb instead).
  const contentRef = useRef(null);
  const [thumb, setThumb] = useState({ top: 0, height: 0, visible: false });
  const dragState = useRef(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const recalc = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight <= clientHeight) { setThumb(t => t.visible ? { ...t, visible: false } : t); return; }
      const trackHeight = clientHeight;
      const height = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
      const top = (scrollTop / (scrollHeight - clientHeight)) * (trackHeight - height);
      setThumb({ top, height, visible: true });
    };

    let raf = null;
    const scheduleRecalc = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; recalc(); });
    };

    recalc();
    el.addEventListener("scroll", recalc);
    const ro = new ResizeObserver(scheduleRecalc);
    ro.observe(el);
    const mo = new MutationObserver(scheduleRecalc);
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", scheduleRecalc);
    return () => {
      el.removeEventListener("scroll", recalc);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", scheduleRecalc);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const onThumbMouseDown = (e) => {
    e.preventDefault();
    dragState.current = { startY: e.clientY, startScrollTop: contentRef.current.scrollTop };
    const onMove = (ev) => {
      const el = contentRef.current;
      if (!el || !dragState.current) return;
      const { scrollHeight, clientHeight } = el;
      const trackHeight = clientHeight;
      const deltaY = ev.clientY - dragState.current.startY;
      const scrollableDist = scrollHeight - clientHeight;
      const thumbTravel = trackHeight - thumb.height;
      if (thumbTravel <= 0) return;
      el.scrollTop = dragState.current.startScrollTop + (deltaY / thumbTravel) * scrollableDist;
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      localStorage.setItem("bc_avatar", ev.target.result);
      setAvatar(ev.target.result); setProfileOpen(false);
    };
    reader.readAsDataURL(file);
  };

  const [broadcast, setBroadcast] = useState(null);
  useEffect(() => {
    const API = (process.env.REACT_APP_API_URL || "");
    const token = () => localStorage.getItem("token") || "";
    async function fetchBroadcast() {
      try {
        const r = await fetch(API + "/api/broadcast/active", { headers: { Authorization: "Bearer " + token() } });
        if (r.ok) { const d = await r.json(); setBroadcast(d.message || null); }
        else setBroadcast(null);
      } catch { setBroadcast(null); }
    }
    fetchBroadcast();
    const i = setInterval(fetchBroadcast, 15000);
    return () => clearInterval(i);
  }, []);

  const isActive = (path) => {
    if (path === "/") return page === "/";
    return page === path || page.startsWith(path + "/");
  };

  const uname = userInfo.username || "Admin";

  const handleNavClick = (path) => { navigate(path); };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'Manrope',sans-serif;background:#f4f3f0;-webkit-font-smoothing:antialiased;color:#292524;}
        ::-webkit-scrollbar{width:6px;}::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.12);border-radius:4px;}
        @keyframes floatUp{from{opacity:0;transform:translateY(16px);}to{opacity:1;transform:translateY(0);}}
        @keyframes pulse{0%,100%{box-shadow:0 0 0 3px rgba(34,197,94,.25)}50%{box-shadow:0 0 0 5px rgba(34,197,94,.06)}}
        @keyframes ddOpen{from{opacity:0;transform:translateY(-6px);}to{opacity:1;transform:translateY(0);}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}

        /* Layout shell — top navbar, no sidebar */
        .adm-shell{display:flex;flex-direction:column;min-height:100vh;position:relative;z-index:1;}

        /* Topbar (now also carries the nav + logo — the sidebar is gone) */
        .adm-topbar{
          background:#fff;
          border-bottom:1px solid #e7e5e4;
          padding:0 24px;height:64px;display:flex;align-items:center;justify-content:space-between;
          position:sticky;top:0;z-index:150;
          gap:16px;
        }
        .adm-topbar-left{display:flex;align-items:center;gap:22px;min-width:0;}
        .adm-logo{display:flex;align-items:center;gap:10px;flex-shrink:0;}
        .adm-logo-mark{
          width:34px;height:34px;border-radius:10px;
          background:#ffb648;
          display:flex;align-items:center;justify-content:center;font-size:17px;flex-shrink:0;
          box-shadow:0 4px 14px rgba(255,182,72,.4);
        }
        .adm-logo-title{font-size:14px;font-weight:800;color:#1c1917;letter-spacing:-.2px;line-height:1.1;}
        .adm-logo-sub{font-size:8.5px;color:#a8a29e;letter-spacing:2px;text-transform:uppercase;margin-top:1px;}

        .adm-nav{display:flex;align-items:center;gap:2px;overflow-x:auto;scrollbar-width:none;}
        .adm-nav::-webkit-scrollbar{display:none;}
        .adm-nav-section{display:none;}
        .adm-nav-item{
          display:flex;align-items:center;gap:7px;padding:9px 15px;cursor:pointer;white-space:nowrap;
          color:#57534e;font-size:13.5px;font-weight:700;
          transition:all .15s;border-radius:9px;
        }
        .adm-nav-item:hover{color:#1c1917;background:#f4f3f0;}
        .adm-nav-item.active{
          color:#17301f;font-weight:800;
          background:#fff1dc;
        }
        .adm-nav-badge{
          margin-left:2px;background:#ffb648;color:#1c1917;
          font-size:10px;font-weight:800;padding:1px 7px;border-radius:20px;
        }

        .adm-sidebar-bottom{display:none;}
        .adm-status-row{
          display:flex;align-items:center;gap:8px;
          background:#f4f3f0;border:1px solid #e7e5e4;
          border-radius:10px;padding:8px 12px;font-size:12px;
        }
        .adm-pulse-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;}
        .adm-pulse-green{background:#22c55e;animation:pulse 2s infinite;}
        .adm-pulse-gray{background:#d6d3d1;}

        .adm-hamburger{display:none;}

        /* Main area */
        .adm-main{flex:1;min-width:0;display:flex;flex-direction:column;}

        .adm-topbar-title{font-size:16px;font-weight:800;color:#1c1917;letter-spacing:-.3px;}

        .adm-content-wrap{flex:1;min-width:0;min-height:0;position:relative;display:flex;overflow:hidden;}
        .adm-content{
          padding:26px 28px;flex:1;min-height:0;overflow-y:auto;animation:fadeIn .2s ease;
          scrollbar-width:none;max-width:1320px;width:100%;margin:0 auto;
        }
        .adm-content::-webkit-scrollbar{display:none;}
        .adm-content-scrollbar-thumb{
          position:absolute;right:4px;width:8px;border-radius:6px;
          background:#d6d3d1;cursor:pointer;
          transition:background .15s;
        }
        .adm-content-scrollbar-thumb:hover{background:#a8a29e;}

        /* Cards */
        .adm-card{
          background:#fff;
          border:1px solid #e7e5e4;
          border-radius:16px;
          box-shadow:0 1px 2px rgba(28,25,23,0.04),0 8px 24px rgba(28,25,23,0.04);
          transition:border-color .2s,box-shadow .2s,transform .2s;
        }
        .adm-card:hover{
          border-color:#d6d3d1;
          box-shadow:0 4px 10px rgba(28,25,23,0.05),0 16px 36px rgba(28,25,23,0.07);
        }
        .adm-card-pad{padding:20px;}
        .adm-card-header{
          padding:14px 20px;border-bottom:1px solid #f0efed;
          display:flex;align-items:center;justify-content:space-between;
          border-radius:16px 16px 0 0;
        }
        .adm-card-title{font-size:14px;font-weight:800;color:#292524;letter-spacing:-.1px;display:flex;align-items:center;gap:9px;}
        .adm-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;display:inline-block;}

        /* Stat cards */
        .adm-stat-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:20px;}
        .adm-stat-card{
          background:#fff;border:1px solid #e7e5e4;border-radius:16px;
          box-shadow:0 1px 2px rgba(28,25,23,0.04),0 8px 24px rgba(28,25,23,0.04);
          padding:22px 22px 18px;position:relative;overflow:hidden;
          transition:border-color .2s,box-shadow .2s,transform .22s;
          animation:floatUp .4s ease both;
        }
        .adm-stat-card:hover{transform:translateY(-3px);}
        .adm-stat-card[data-c="blue"]:hover  {border-color:#93c5fd!important;box-shadow:0 16px 40px rgba(59,130,246,0.14)!important;}
        .adm-stat-card[data-c="green"]:hover {border-color:#86efac!important;box-shadow:0 16px 40px rgba(34,197,94,0.14)!important;}
        .adm-stat-card[data-c="amber"]:hover {border-color:#ffd291!important;box-shadow:0 16px 40px rgba(255,182,72,0.2)!important;}
        .adm-stat-card[data-c="purple"]:hover{border-color:#d8b4fe!important;box-shadow:0 16px 40px rgba(147,51,234,0.16)!important;}
        .adm-stat-bar{position:absolute;top:0;left:0;right:0;height:3px;}
        .adm-stat-watermark{position:absolute;bottom:8px;right:14px;font-size:48px;opacity:.06;line-height:1;font-weight:900;}
        .adm-stat-val{font-size:44px;font-weight:900;letter-spacing:-2.5px;line-height:1;margin-bottom:8px;color:#1c1917;}
        .adm-stat-lbl{font-size:11px;letter-spacing:1.6px;text-transform:uppercase;font-weight:800;color:#78716c;}

        /* Tables */
        .adm-table-wrap{overflow-x:auto;}
        .adm-table{width:100%;border-collapse:collapse;}
        .adm-table th{text-align:left;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.9px;color:#a8a29e;padding:10px 16px;border-bottom:1px solid #f0efed;}
        .adm-table td{padding:12px 16px;border-bottom:1px solid #f6f5f4;font-size:13px;vertical-align:middle;color:#44403c;}
        .adm-table tr:last-child td{border-bottom:none;}
        .adm-table tr:hover td{background:#faf9f7;}

        /* Badges */
        .adm-badge{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700;white-space:nowrap;letter-spacing:.3px;}
        .adm-badge-green{background:#f0fdf4;color:#15803d;border:1px solid #bbf7d0;}
        .adm-badge-red{background:#fef2f2;color:#b91c1c;border:1px solid #fecaca;}
        .adm-badge-amber{background:#fffbeb;color:#b45309;border:1px solid #fde68a;}
        .adm-badge-blue{background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;}
        .adm-badge-gray{background:#f5f5f4;color:#57534e;border:1px solid #e7e5e4;}

        /* Progress bar */
        .adm-prog{height:4px;background:#f0efed;border-radius:3px;overflow:hidden;}
        .adm-prog-fill{height:100%;border-radius:3px;transition:width .5s ease;}

        /* Buttons */
        .adm-btn{
          display:inline-flex;align-items:center;gap:6px;
          padding:7px 14px;border-radius:9px;border:1px solid #e7e5e4;
          background:#fff;color:#57534e;
          font-size:13px;font-weight:700;cursor:pointer;
          font-family:'Manrope',sans-serif;transition:all .15s;
        }
        .adm-btn:hover{background:#faf9f7;color:#1c1917;border-color:#d6d3d1;}
        .adm-btn:disabled{opacity:.5;cursor:not-allowed;}
        .adm-btn-primary{
          background:#17301f;color:#fff;
          border:none;box-shadow:0 4px 14px rgba(23,48,31,.22);
        }
        .adm-btn-primary:hover{transform:translateY(-1px);box-shadow:0 8px 20px rgba(23,48,31,.3);background:#1f3d28;color:#fff;}
        .adm-btn-danger{background:#fef2f2;color:#b91c1c;border-color:#fecaca;}
        .adm-btn-danger:hover{background:#fee2e2;color:#991b1b;}
        .adm-btn-success{background:#f0fdf4;color:#15803d;border-color:#bbf7d0;}
        .adm-btn-sm{padding:5px 11px;font-size:12px;}
        .adm-btn-ghost{background:transparent;border-color:transparent;color:#a8a29e;}
        .adm-btn-ghost:hover{color:#57534e;background:#f4f3f0;border-color:transparent;}

        /* Forms */
        .adm-input{
          width:100%;background:#fff;border:1.5px solid #e7e5e4;
          color:#1c1917;border-radius:10px;padding:9px 13px;font-size:13px;
          font-family:'Manrope',sans-serif;outline:none;box-sizing:border-box;transition:border-color .18s,box-shadow .18s;
        }
        .adm-input:focus{border-color:#ffb648;box-shadow:0 0 0 3px rgba(255,182,72,0.15);}
        .adm-input::placeholder{color:#a8a29e;}
        select.adm-input{appearance:none;cursor:pointer;}
        .adm-label{display:block;font-size:10px;font-weight:800;letter-spacing:1.1px;text-transform:uppercase;color:#78716c;margin-bottom:5px;}
        .adm-form-row{margin-bottom:15px;}
        .adm-form-err{color:#b91c1c;font-size:12px;background:#fef2f2;border:1px solid #fecaca;padding:8px 12px;border-radius:8px;margin-top:8px;}

        /* Modal */
        .adm-modal-bg{position:fixed;inset:0;background:rgba(28,25,23,.5);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:500;opacity:0;pointer-events:none;transition:opacity .15s;}
        .adm-modal-bg.open{opacity:1;pointer-events:all;}
        .adm-modal{
          background:#fff;
          border:1px solid #e7e5e4;border-radius:18px;
          box-shadow:0 24px 70px rgba(28,25,23,.25);
          padding:28px;width:500px;max-width:calc(100vw - 40px);
          transform:translateY(14px);transition:transform .18s;animation:floatUp .18s ease;
        }
        .adm-modal-bg.open .adm-modal{transform:translateY(0);}
        .adm-modal-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:22px;}
        .adm-modal-title{font-size:16px;font-weight:800;color:#1c1917;}
        .adm-modal-footer{display:flex;gap:10px;justify-content:flex-end;margin-top:22px;padding-top:18px;border-top:1px solid #f0efed;}

        /* Agent cards */
        .adm-agent-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(290px,1fr));gap:16px;}
        .adm-agent-card{padding:18px;display:flex;flex-direction:column;gap:13px;border-radius:16px;}

        /* Log box */
        .adm-log-box{
          background:#1c1917;border:1px solid #292524;
          border-radius:10px;padding:12px 14px;height:260px;overflow-y:auto;
          font-family:'JetBrains Mono',monospace;font-size:11.5px;
        }
        .adm-log-line{padding:2px 0;border-bottom:1px solid rgba(255,255,255,0.04);display:flex;gap:8px;align-items:baseline;}
        .adm-log-line:last-child{border-bottom:none;}
        .adm-log-ts{color:#78716c;flex-shrink:0;font-size:10.5px;}
        .adm-log-src{color:#ffb648;flex-shrink:0;}
        .adm-log-msg{color:#d6d3d1;}
        .adm-log-line.warn .adm-log-msg{color:#fbbf24;}
        .adm-log-line.error .adm-log-msg{color:#f87171;}

        /* Admin tabs */
        .adm-tabs{display:flex;gap:4px;margin-bottom:18px;background:#f4f3f0;border:1px solid #e7e5e4;border-radius:12px;padding:4px;width:fit-content;}
        .adm-tab{display:flex;align-items:center;gap:6px;padding:7px 16px;border-radius:8px;font-size:13px;font-weight:700;color:#a8a29e;cursor:pointer;background:none;border:none;font-family:'Manrope',sans-serif;transition:all .13s;letter-spacing:.1px;}
        .adm-tab:hover{background:#fff;color:#57534e;}
        .adm-tab.active{background:#fff;color:#1c1917;box-shadow:0 1px 3px rgba(28,25,23,0.08);}

        /* Empty state */
        .adm-empty{text-align:center;padding:36px 20px;color:#a8a29e;font-size:13px;}

        /* TD actions */
        .adm-td-actions{display:flex;gap:6px;align-items:center;flex-wrap:wrap;}

        /* Mono */
        .adm-mono{font-family:'JetBrains Mono',monospace;font-size:11px;color:#292524;font-weight:600;}
        .adm-mono-box{
          background:#faf9f7;border:1px solid #e7e5e4;
          border-radius:9px;padding:10px 13px;font-family:'JetBrains Mono',monospace;
          font-size:12px;word-break:break-all;user-select:all;line-height:1.75;color:#292524;
        }

        /* ── Broadcast Ticker ── */
        .bc-ticker-wrap{
          position:fixed;top:0;left:0;right:0;z-index:9999;
          background:#fff1dc;
          border-bottom:1px solid #ffd291;
          height:36px;overflow:hidden;display:flex;align-items:center;
        }
        .bc-ticker-label{
          flex-shrink:0;padding:0 16px;
          font-size:11px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;
          color:#b45309;border-right:1px solid #ffd291;
          white-space:nowrap;display:flex;align-items:center;gap:6px;
        }
        .bc-ticker-track{flex:1;overflow:hidden;position:relative;}
        .bc-ticker-inner{
          display:inline-flex;white-space:nowrap;
          animation:ticker-scroll 20s linear infinite;
        }
        .bc-ticker-inner:hover{animation-play-state:paused;}
        .bc-ticker-text{
          padding:0 60px;font-size:13px;font-weight:700;
          color:#78350f;letter-spacing:.2px;
        }
        @keyframes ticker-scroll{
          0%{transform:translateX(0);}
          100%{transform:translateX(-50%);}
        }
        .bc-ticker-active .adm-topbar{top:36px;}

        /* ── Responsive ── */
        @media(max-width:960px){
          .adm-stat-grid{grid-template-columns:repeat(2,1fr);}
        }

        @media(max-width:768px){
          .adm-topbar{padding:0 14px;height:auto;flex-wrap:wrap;row-gap:8px;padding-top:10px;padding-bottom:10px;}
          .adm-topbar-left{width:100%;justify-content:space-between;}
          .adm-nav{width:100%;justify-content:flex-start;}
          .adm-content{padding:14px 14px;}
          .adm-content-scrollbar-thumb{display:none;}
          .adm-stat-grid{grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:14px;}
          .adm-stat-val{font-size:34px;}
          .adm-offline-chip{display:none;}
          .adm-topbar-right{gap:6px!important;}
          .adm-signout-label{display:none;}
        }

        @media(max-width:480px){
          .adm-stat-grid{grid-template-columns:repeat(2,1fr);gap:8px;}
          .adm-stat-card{padding:14px 14px 12px;}
          .adm-stat-val{font-size:26px;letter-spacing:-1.5px;}
          .adm-stat-lbl{font-size:9px;letter-spacing:1px;}
          .adm-stat-watermark{font-size:34px;}
          .adm-content{padding:10px 10px;}
        }
      `}</style>

      {broadcast && (
        <div className="bc-ticker-wrap">
          <div className="bc-ticker-label">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 8.5c0 2.485-4.477 4.5-10 4.5S2 10.985 2 8.5 6.477 4 12 4s10 2.015 10 4.5z"/><path d="M2 8.5c0 2.485 4.477 4.5 10 4.5s10-2.015 10-4.5"/><path d="M2 12c0 2.485 4.477 4.5 10 4.5S22 14.485 22 12"/><path d="M2 15.5c0 2.485 4.477 4.5 10 4.5s10-2.015 10-4.5"/></svg>
            Notice
          </div>
          <div className="bc-ticker-track">
            <div className="bc-ticker-inner">
              <span className="bc-ticker-text">{broadcast}</span>
              <span className="bc-ticker-text">{broadcast}</span>
            </div>
          </div>
        </div>
      )}
      <div className="adm-shell" style={broadcast ? {marginTop:36} : {}}>
        {/* ── Topbar (logo + nav + profile, no sidebar) ── */}
        <div className="adm-topbar">
          <div className="adm-topbar-left">
            <div className="adm-logo">
              <div className="adm-logo-mark">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#17301f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4.5L6 21l1.5-7.5L2 9h7z" />
                </svg>
              </div>
              <div>
                <div className="adm-logo-title">Meridian</div>
                <div className="adm-logo-sub">Control Center</div>
              </div>
            </div>

            <nav className="adm-nav">
              {NAV.filter(n => !n.adminOnly || userInfo.role === "admin").map(n => (
                <div
                  key={n.id}
                  className={`adm-nav-item${isActive(n.path) ? " active" : ""}`}
                  onClick={() => handleNavClick(n.path)}
                >
                  {n.icon}
                  {n.label}
                </div>
              ))}
            </nav>
          </div>

          <div className="adm-topbar-right" style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            {/* Server status chip — hidden on mobile */}
            <div className="adm-offline-chip" style={{ display: "flex", alignItems: "center", gap: 7, background: serverOnline ? "#f0fdf4" : "#fef2f2", border: `1px solid ${serverOnline ? "#bbf7d0" : "#fecaca"}`, borderRadius: 30, padding: "5px 14px", transition: "all 0.4s" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: serverOnline ? "#22c55e" : "#ef4444", flexShrink: 0, display: "inline-block", animation: serverOnline ? "pulse 2s infinite" : "none" }} />
              <span style={{ fontSize: 12, color: serverOnline ? "#15803d" : "#b91c1c", fontWeight: 700, transition: "color 0.4s" }}>{serverOnline ? "Online" : "Offline"}</span>
            </div>

            {/* Avatar / user */}
            <div style={{ position: "relative" }}>
              <div
                onClick={() => setProfileOpen(o => !o)}
                style={{ display: "flex", alignItems: "center", gap: 9, background: "#fff", border: "1px solid #e7e5e4", borderRadius: 32, padding: "5px 14px 5px 6px", cursor: "pointer" }}
              >
                {avatar
                  ? <img src={avatar} alt="av" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }} />
                  : <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#17301f", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#ffb648" }}>{uname[0]?.toUpperCase()}</div>
                }
                <span style={{ fontSize: 13, color: "#57534e", fontWeight: 600 }}>{uname}</span>
              </div>
              {profileOpen && (
                <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: "#fff", border: "1px solid #e7e5e4", borderRadius: 14, padding: 16, width: 200, zIndex: 300, boxShadow: "0 20px 50px rgba(28,25,23,.18)" }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#a8a29e", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 10 }}>Account</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1c1917", marginBottom: 2 }}>{uname}</div>
                  <div style={{ fontSize: 11, color: "#78716c", marginBottom: 12 }}>{userInfo.role === "admin" ? "👑 Admin" : "👤 User"}</div>
                  <button className="adm-btn adm-btn-sm" style={{ width: "100%", justifyContent: "center", marginBottom: 6 }} onClick={() => fileRef.current.click()}>
                    {avatar ? "Change Photo" : "Upload Photo"}
                  </button>
                  {avatar && <button className="adm-btn adm-btn-sm adm-btn-danger" style={{ width: "100%", justifyContent: "center" }} onClick={() => { localStorage.removeItem("bc_avatar"); setAvatar(null); setProfileOpen(false); }}>Remove</button>}
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarChange} />
                </div>
              )}
            </div>

            <button
              onClick={onLogout}
              style={{ background: "transparent", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 9, padding: "7px 16px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, transition: "all .15s", display: "flex", alignItems: "center", gap: 6 }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              <span className="adm-signout-label">Sign out</span>
            </button>
          </div>
        </div>

        {/* ── Main ── */}
        <div className="adm-main">
          <div className="adm-content-wrap">
            <div className="adm-content" ref={contentRef}>
              {children}
            </div>
            {thumb.visible && (
              <div
                className="adm-content-scrollbar-thumb"
                style={{ top: thumb.top, height: thumb.height }}
                onMouseDown={onThumbMouseDown}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Icons
function GridIcon()    { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>; }
function PayIcon()     { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>; }
function BroadcastIcon(){ return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 8.5c0 2.485-4.477 4.5-10 4.5S2 10.985 2 8.5 6.477 4 12 4s10 2.015 10 4.5z"/><path d="M2 8.5c0 2.485 4.477 4.5 10 4.5s10-2.015 10-4.5"/><path d="M2 12c0 2.485 4.477 4.5 10 4.5S22 14.485 22 12"/><path d="M2 15.5c0 2.485 4.477 4.5 10 4.5s10-2.015 10-4.5"/></svg>; }
function ShieldIcon()  { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>; }
